import React, { useState, useMemo, memo } from 'react'
import {
  Box,
  Text,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Flex,
  Select,
  Tooltip,
  Skeleton,
  Stack,
  VStack,
  Button,
  ButtonGroup,
  useToast,
  Link,
} from '@chakra-ui/react'
import { FiSearch, FiFileText, FiDownload, FiCheck, FiX, FiExternalLink } from 'react-icons/fi'
import { FixedSizeList as List } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'
import { usePdfGeneratorNew } from '../../../../hooks/usePdfGenerator'
import { useExcelGenerator } from '../../../../hooks/useExcelGenerator'
import { format } from 'date-fns'

// Componente de linha otimizado com memo para evitar re-renders desnecessários
const ProductRow = memo(({ product, style }) => (
  <div style={{ ...style, display: 'flex', alignItems: 'center', borderBottom: '1px solid #E2E8F0' }}>
    <Box flex="0 0 230px" px={3} py={2} display="flex" alignItems="center" justifyContent="center">
      <Text isTruncated fontFamily="mono">
        {format(new Date(product.ultima_atualizacao), 'dd/MM/yyyy HH:mm:ss')}
      </Text>
    </Box>
    <Box flex="0 0 100px" px={3} py={2} display="flex" alignItems="center" justifyContent="center">
      <Text isTruncated fontFamily="mono">
        {product.codigo_produto}
      </Text>
    </Box>

    <Box flex="1" px={3} py={2} display="flex" alignItems="center">
      <Tooltip label={product.codigo_fornecedor} placement="top" hasArrow openDelay={500}>
        <Text isTruncated fontFamily="mono">
          {product.codigo_fornecedor}
        </Text>
      </Tooltip>
    </Box>

    <Box flex="0 0 100px" px={3} py={2} display="flex" alignItems="center" justifyContent="center">
      <Badge
        colorScheme={product.fornecedor === 'SPOT' ? 'blue' : product.fornecedor === 'ASIA' ? 'purple' : 'gray'}
        borderRadius="md"
        px={2}
        py={0.5}
      >
        {product.fornecedor || '-'}
      </Badge>
    </Box>

    <Box flex="0 0 200px" px={3} py={2} display="flex" alignItems="center" justifyContent="center">
      <Tooltip label={product.produto_atualizado === 'S' ? 'Difal atualizado' : 'Difal não atualizado'} placement="top" hasArrow>
        <Box display="inline-flex" alignItems="center" justifyContent="center">
          {product.produto_atualizado === 'S' ? <Icon as={FiCheck} color="green.500" boxSize={5} /> : <Icon as={FiX} color="red.500" boxSize={5} />}
        </Box>
      </Tooltip>
    </Box>

    <Box flex="0 0 80px" px={3} py={2} display="flex" alignItems="center" justifyContent="center">
      <Tooltip label={product.calculo_difal === 'S' ? 'Tem difal' : 'Não tem difal'} placement="top" hasArrow>
        <Box display="inline-flex" alignItems="center" justifyContent="center">
          {product.calculo_difal === 'S' ? <Icon as={FiCheck} color="green.500" boxSize={5} /> : <Icon as={FiX} color="red.500" boxSize={5} />}
        </Box>
      </Tooltip>
    </Box>

    <Box flex="0 0 80px" px={3} py={2} display="flex" alignItems="center" justifyContent="center">
      <Tooltip label={product.atualiza_preco === 'S' ? 'Preço atualizado' : 'Preço não atualizado'} placement="top" hasArrow>
        <Box display="inline-flex" alignItems="center" justifyContent="center">
          {product.atualiza_preco === 'S' ? <Icon as={FiCheck} color="green.500" boxSize={5} /> : <Icon as={FiX} color="red.500" boxSize={5} />}
        </Box>
      </Tooltip>
    </Box>

    <Box flex="0 0 80px" px={3} py={2} display="flex" alignItems="center" justifyContent="center">
      <Tooltip label={product.atualiza_estoque === 'S' ? 'Estoque atualizado' : 'Estoque não atualizado'} placement="top" hasArrow>
        <Box display="inline-flex" alignItems="center" justifyContent="center">
          {product.atualiza_estoque === 'S' ? <Icon as={FiCheck} color="green.500" boxSize={5} /> : <Icon as={FiX} color="red.500" boxSize={5} />}
        </Box>
      </Tooltip>
    </Box>

    <Box flex="0 0 60px" px={3} py={2} display="flex" alignItems="center" justifyContent="center">
      {product.url_produto ? (
        <Link href={product.url_produto} isExternal aria-label="Link externo">
          <Icon as={FiExternalLink} color="blue.500" boxSize={5} />
        </Link>
      ) : (
        <Icon as={FiExternalLink} color="red.500" boxSize={5} />
      )}
    </Box>
  </div>
))

ProductRow.displayName = 'ProductRow'

export function ProductTable({ products, isLoading, title, colorScheme = 'green' }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState('ultima_atualizacao')
  const [sortDirection, setSortDirection] = useState('desc')
  const { generatePDF, loading: loadingPdf } = usePdfGeneratorNew()
  const { generateExcel, loading: loadingExcel } = useExcelGenerator()
  const toast = useToast()

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const filteredProducts = useMemo(() => {
    if (!products) return []

    const filtered =
      searchTerm.trim() === ''
        ? products
        : products.filter((product) => {
            const lowercaseSearch = searchTerm.toLowerCase()
            return (
              (product.codigo_produto?.toString() || '').toLowerCase().includes(lowercaseSearch) ||
              (product.codigo_fornecedor?.toString() || '').toLowerCase().includes(lowercaseSearch) ||
              (product.fornecedor?.toString() || '').toLowerCase().includes(lowercaseSearch)
            )
          })

    // Depois ordenar - O(n log n)
    return [...filtered].sort((a, b) => {
      if (sortField === 'preco') {
        return sortDirection === 'asc' ? (Number(a.preco) || 0) - (Number(b.preco) || 0) : (Number(b.preco) || 0) - (Number(a.preco) || 0)
      }

      if (sortField === 'estoque') {
        return sortDirection === 'asc' ? (Number(a.estoque) || 0) - (Number(b.estoque) || 0) : (Number(b.estoque) || 0) - (Number(a.estoque) || 0)
      }

      if (sortField === 'ultima_atualizacao') {
        const dateA = new Date(a[sortField] || '')
        const dateB = new Date(b[sortField] || '')
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA
      }

      // Para campos booleanos como calculo_difal, atualiza_preco, atualiza_estoque
      if (['calculo_difal', 'atualiza_preco', 'atualiza_estoque'].includes(sortField)) {
        return sortDirection === 'asc'
          ? a[sortField] === b[sortField]
            ? 0
            : a[sortField]
              ? -1
              : 1
          : a[sortField] === b[sortField]
            ? 0
            : a[sortField]
              ? 1
              : -1
      }

      // Para campos de texto
      const valA = (a[sortField]?.toString() || '').toLowerCase()
      const valB = (b[sortField]?.toString() || '').toLowerCase()
      return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA)
    })
  }, [products, searchTerm, sortField, sortDirection])

  const filteredCount = useMemo(() => filteredProducts.length, [filteredProducts])

  const handleExportPdf = () => {
    if (!filteredProducts.length) {
      toast({
        title: 'Nenhum dado para exportar',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    try {
      const headers = [
        'Data Atualização',
        'Código',
        'Código Fornecedor',
        'Fornecedor',
        'Difal Atualizado',
        'Difal',
        'Atualiz. Preço',
        'Atualiz. Estoque',
        'Link',
      ]

      const mappedData = filteredProducts.map((product) => [
        product.ultima_atualizacao?.toString() || '',
        product.codigo_produto?.toString() || '',
        product.codigo_fornecedor?.toString() || '',
        product.fornecedor?.toString() || '-',
        product.produto_atualizado === 'S' ? 'Sim' : 'Não',
        product.calculo_difal === 'S' ? 'Sim' : 'Não',
        product.atualiza_preco === 'S' ? 'Sim' : 'Não',
        product.atualiza_estoque === 'S' ? 'Sim' : 'Não',
        product.url_produto?.toString() || '-',
      ])

      generatePDF(headers, mappedData, `Produtos Fornecedor - ${title}`)

      toast({
        title: 'PDF exportado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    } catch (error) {
      console.error('Erro ao exportar PDF:', error)
      toast({
        title: 'Erro ao exportar PDF',
        description: 'Ocorreu um problema durante a exportação do arquivo.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const handleExportExcel = async () => {
    if (!filteredProducts.length) {
      toast({
        title: 'Nenhum dado para exportar',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    try {
      const headers = [
        'Data Atualização',
        'Código',
        'Código Fornecedor',
        'Fornecedor',
        'Difal Atualizado',
        'Difal',
        'Atualiz. Preço',
        'Atualiz. Estoque',
        'Link',
      ]

      const mappedData = filteredProducts.map((product) => [
        product.ultima_atualizacao?.toString() || '',
        product.codigo_produto?.toString() || '',
        product.codigo_fornecedor?.toString() || '',
        product.fornecedor?.toString() || '-',
        product.produto_atualizado === 'S' ? 'Sim' : 'Não',
        product.calculo_difal === 'S' ? 'Sim' : 'Não',
        product.atualiza_preco === 'S' ? 'Sim' : 'Não',
        product.atualiza_estoque === 'S' ? 'Sim' : 'Não',
        product.url_produto?.toString() || '-',
      ])

      await generateExcel(headers, mappedData, `Produtos Fornecedor - ${title}`)

      toast({
        title: 'Excel exportado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    } catch (error) {
      console.error('Erro ao exportar Excel:', error)
      toast({
        title: 'Erro ao exportar Excel',
        description: 'Ocorreu um problema durante a exportação do arquivo.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  if (isLoading) {
    return (
      <Box bg="white" borderRadius="md" boxShadow="sm" p={4}>
        <Text fontWeight="bold" mb={4} fontSize="lg">
          {title}
        </Text>
        <Stack>
          <Skeleton height="40px" />
          <Skeleton height="40px" />
          <Skeleton height="40px" />
          <Skeleton height="40px" />
        </Stack>
      </Box>
    )
  }

  return (
    <Box bg="white" borderRadius="md" boxShadow="sm" p={4}>
      <Flex justifyContent="space-between" alignItems="center" mb={4} flexWrap="wrap" gap={2}>
        <Text fontWeight="bold" fontSize="lg">
          {title}{' '}
          <Badge colorScheme={colorScheme} ml={2}>
            {filteredCount}
          </Badge>
        </Text>

        <Flex flexWrap="wrap" gap={2}>
          <InputGroup size="sm" maxW="300px" mr={2}>
            <InputLeftElement pointerEvents="none">
              <Icon as={FiSearch} color="gray.400" />
            </InputLeftElement>
            <Input placeholder="Buscar produto" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} borderRadius="md" />
          </InputGroup>

          <Select
            size="sm"
            maxW="200px"
            value={`${sortField}|${sortDirection}`}
            onChange={(e) => {
              const [field, direction] = e.target.value.split('|')
              setSortField(field)
              setSortDirection(direction)
            }}
          >
            <option value="ultima_atualizacao|desc">Data (mais recente)</option>
            <option value="ultima_atualizacao|asc">Data (mais antiga)</option>
            <option value="codigo_produto|asc">Código (A-Z)</option>
            <option value="codigo_produto|desc">Código (Z-A)</option>
            <option value="codigo_fornecedor|asc">Cód. Fornecedor (A-Z)</option>
            <option value="codigo_fornecedor|desc">Cód. Fornecedor (Z-A)</option>
            <option value="fornecedor|asc">Fornecedor (A-Z)</option>
            <option value="fornecedor|desc">Fornecedor (Z-A)</option>
            <option value="preco|asc">Preço (menor-maior)</option>
            <option value="preco|desc">Preço (maior-menor)</option>
            <option value="estoque|asc">Estoque (menor-maior)</option>
            <option value="estoque|desc">Estoque (maior-menor)</option>
            <option value="calculo_difal|asc">Difal (Sim-Não)</option>
            <option value="calculo_difal|desc">Difal (Não-Sim)</option>
            <option value="atualiza_preco|asc">Atualiz. Preço (Sim-Não)</option>
            <option value="atualiza_preco|desc">Atualiz. Preço (Não-Sim)</option>
            <option value="atualiza_estoque|asc">Atualiz. Estoque (Sim-Não)</option>
            <option value="atualiza_estoque|desc">Atualiz. Estoque (Não-Sim)</option>
          </Select>

          {/* Botões de Exportação */}
          <ButtonGroup size="sm" ml={[0, 0, 2]} mt={[2, 2, 0]} w={['100%', 'auto']} justifyContent={['flex-end', 'flex-start']}>
            <Button
              leftIcon={<Icon as={FiFileText} />}
              colorScheme="blue"
              variant="outline"
              onClick={handleExportPdf}
              isLoading={loadingPdf}
              loadingText="Exportando"
              isDisabled={!filteredCount || loadingExcel}
            >
              PDF
            </Button>
            <Button
              leftIcon={<Icon as={FiDownload} />}
              colorScheme="green"
              variant="outline"
              onClick={handleExportExcel}
              isLoading={loadingExcel}
              loadingText="Exportando"
              isDisabled={!filteredCount || loadingPdf}
            >
              Excel
            </Button>
          </ButtonGroup>
        </Flex>
      </Flex>

      <Box overflowX="auto" borderWidth="1px" borderRadius="md">
        {/* Cabeçalho da tabela - fixo */}
        <Flex bg="gray.50" p={2} borderBottomWidth="1px" position="sticky" top={0} zIndex={1}>
          <Box flex="0 0 220px" px={3} py={2} fontWeight="bold" textAlign="center" cursor="pointer" onClick={() => handleSort('ultima_atualizacao')}>
            Data Atualização {sortField === 'ultima_atualizacao' && (sortDirection === 'asc' ? '↑' : '↓')}
          </Box>
          <Box flex="0 0 100px" px={3} py={2} fontWeight="bold" textAlign="center" cursor="pointer" onClick={() => handleSort('codigo_produto')}>
            Código {sortField === 'codigo_produto' && (sortDirection === 'asc' ? '↑' : '↓')}
          </Box>
          <Box flex="1" px={3} py={2} fontWeight="bold" cursor="pointer" onClick={() => handleSort('codigo_fornecedor')}>
            Código Fornecedor {sortField === 'codigo_fornecedor' && (sortDirection === 'asc' ? '↑' : '↓')}
          </Box>
          <Box flex="0 0 100px" px={3} py={2} fontWeight="bold" textAlign="center" cursor="pointer" onClick={() => handleSort('fornecedor')}>
            Fornecedor {sortField === 'fornecedor' && (sortDirection === 'asc' ? '↑' : '↓')}
          </Box>

          {/* Cabeçalho de Difal Atualizado com tamanho aumentado */}
          <Box flex="0 0 200px" px={3} py={2} fontWeight="bold" textAlign="center" cursor="pointer" onClick={() => handleSort('produto_atualizado')}>
            Difal Atualizado {sortField === 'produto_atualizado' && (sortDirection === 'asc' ? '↑' : '↓')}
          </Box>

          <Box flex="0 0 80px" px={3} py={2} fontWeight="bold" textAlign="center" cursor="pointer" onClick={() => handleSort('calculo_difal')}>
            Difal {sortField === 'calculo_difal' && (sortDirection === 'asc' ? '↑' : '↓')}
          </Box>
          <Box flex="0 0 80px" px={3} py={2} fontWeight="bold" textAlign="center" cursor="pointer" onClick={() => handleSort('atualiza_preco')}>
            Preço {sortField === 'atualiza_preco' && (sortDirection === 'asc' ? '↑' : '↓')}
          </Box>
          <Box flex="0 0 80px" px={3} py={2} fontWeight="bold" textAlign="center" cursor="pointer" onClick={() => handleSort('atualiza_estoque')}>
            Estoque {sortField === 'atualiza_estoque' && (sortDirection === 'asc' ? '↑' : '↓')}
          </Box>
          <Box flex="0 0 60px" px={3} py={2} fontWeight="bold" textAlign="center">
            Link
          </Box>
        </Flex>

        {/* Lista virtualizada com react-window */}
        <Box height="600px" width="100%">
          {filteredCount > 0 ? (
            <AutoSizer>
              {({ height, width }) => (
                <List height={height} itemCount={filteredCount} itemSize={50} width={width}>
                  {({ index, style }) => <ProductRow product={filteredProducts[index]} style={style} />}
                </List>
              )}
            </AutoSizer>
          ) : (
            <VStack py={10}>
              <Text>Nenhum produto encontrado</Text>
            </VStack>
          )}
        </Box>
      </Box>
    </Box>
  )
}

ProductTable.displayName = 'ProductTable'
